/**
 * SupportPage — user-facing helpdesk.
 *
 * Users can:
 *   - Raise a new ticket (category, subject, description)
 *   - Browse their tickets with status filter tabs
 *   - Open a ticket to read the thread and post follow-up replies
 */

import { useEffect, useState } from 'react';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import SegmentedControl from '../components/common/SegmentedControl';
import { Select as FormSelect, inputClass, textareaClass, labelClass } from '../components/common/FormControls';
import { useUI } from '../context/UIContext';
import { ticketAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { ProfileCard, Pill } from '../components/profile/ProfilePrimitives';

// Stale-while-revalidate key (services/viewCache): this route unmounts on nav,
// so the ticket list flashed a loading card over tickets just seen. Keyed by
// the status filter.
const supportTicketsKey = (status) => `support:tickets:${status}`;

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';

const STATUS_TONES = {
  open: 'orange',
  in_progress: 'teal',
  resolved: 'green',
  closed: 'neutral',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const CATEGORIES = [
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'compliance', label: 'Compliance Question' },
  { value: 'account', label: 'Account / Access' },
];

function CreateTicketForm({ onCreated }) {
  const { showToast } = useUI();
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!category || !subject.trim() || !description.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await ticketAPI.createTicket({
        category,
        subject: subject.trim(),
        description: description.trim(),
      });
      if (res.success) {
        showToast('Ticket created successfully', 'success');
        setCategory('');
        setSubject('');
        setDescription('');
        onCreated();
      } else {
        setError(res.message || 'Could not create ticket.');
      }
    } catch (err) {
      setError(err?.message || 'Could not create ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ProfileCard title="Raise a Ticket" subtitle="Describe your issue and we'll get back to you." icon="📝">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div className="px-3 py-2 rounded-lg text-[12px]" style={{ background: '#f7e3e3', color: '#b33030' }}>
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className={labelClass}>
              Category *
            </div>
            <FormSelect
              value={category}
              onChange={setCategory}
              placeholder="Select…"
              options={CATEGORIES}
            />
          </div>
          <div className="sm:col-span-2">
            <div className={labelClass}>
              Subject *
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your issue"
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <div className={labelClass}>
            Description *
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue in detail…"
            rows={5}
            className={textareaClass}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" loading={submitting}>
            {submitting ? 'Creating…' : 'Create Ticket'}
          </Button>
        </div>
      </form>
    </ProfileCard>
  );
}

function TicketList({ tickets, counts, statusFilter, onFilter, onSelect, selectedId }) {
  return (
    <ProfileCard title="My Tickets" subtitle="Track all your support requests." icon="🎫">
      {/* Status filter tabs */}
      <div className="mb-4 overflow-x-auto">
        <SegmentedControl
          value={statusFilter}
          onChange={onFilter}
          options={['all', 'open', 'in_progress', 'resolved', 'closed'].map((s) => ({
            key: s,
            label: s === 'all' ? 'All' : STATUS_LABELS[s],
            count: counts[s] || 0,
          }))}
        />
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-10 text-[13px]" style={{ color: MUTED }}>
          No tickets found.
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <button
              key={ticket._id}
              onClick={() => onSelect(ticket._id)}
              className="w-full text-left border rounded-lg p-4 transition-colors hover:bg-[#faf8f3]"
              style={{
                borderColor: selectedId === ticket._id ? '#145f5f' : BORDER,
                background: selectedId === ticket._id ? '#f0f7f7' : '#fff',
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-mono" style={{ color: MUTED }}>{ticket.ticketNumber}</span>
                    <Pill tone={STATUS_TONES[ticket.status] || 'neutral'}>
                      {STATUS_LABELS[ticket.status]}
                    </Pill>
                  </div>
                  <h4 className="font-semibold text-[13px] truncate" style={{ color: TEXT }}>
                    {ticket.subject}
                  </h4>
                  <p className="text-[11.5px] truncate mt-0.5" style={{ color: MUTED }}>
                    {ticket.description}
                  </p>
                </div>
                <div className="text-[10.5px] whitespace-nowrap" style={{ color: MUTED }}>
                  {new Date(ticket.createdAt).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </ProfileCard>
  );
}

function TicketThread({ ticketId, onBack }) {
  const { showToast } = useUI();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTicket = async () => {
    try {
      const res = await ticketAPI.getTicket(ticketId);
      if (res.success) setTicket(res.data.ticket);
    } catch (err) {
      showToast('Could not load ticket', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await ticketAPI.addReply(ticketId, reply.trim());
      if (res.success) {
        setReply('');
        setTicket(res.data.ticket);
        showToast('Reply sent', 'success');
      }
    } catch (err) {
      showToast('Could not send reply', 'error');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <ProfileCard title="Loading…" icon="⏳">
        <div className="text-[13px] py-6" style={{ color: MUTED }}>Loading ticket details…</div>
      </ProfileCard>
    );
  }

  if (!ticket) {
    return (
      <ProfileCard title="Not Found" icon="❓">
        <div className="text-[13px] py-6" style={{ color: MUTED }}>Ticket not found.</div>
        <button onClick={onBack} className="text-[12px] font-medium" style={{ color: '#145f5f' }}>← Back to tickets</button>
      </ProfileCard>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-[12px] font-medium px-2 py-1 rounded" style={{ color: '#145f5f', border: `1px solid ${BORDER}` }}>
          ← Back
        </button>
        <span className="text-[11px] font-mono" style={{ color: MUTED }}>{ticket.ticketNumber}</span>
      </div>

      {/* Ticket header */}
      <ProfileCard title={ticket.subject} subtitle={`Category: ${CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}`} icon="🎫" action={
        <Pill tone={STATUS_TONES[ticket.status] || 'neutral'}>{STATUS_LABELS[ticket.status]}</Pill>
      }>
        <div className="text-[13px] leading-relaxed" style={{ color: TEXT }}>
          {ticket.description}
        </div>
        <div className="mt-3 flex gap-3 text-[10.5px]" style={{ color: MUTED }}>
          <span>Created {new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          {ticket.assigneeName && <span>Assigned to: {ticket.assigneeName}</span>}
        </div>
      </ProfileCard>

      {/* Replies */}
      <ProfileCard title="Conversation" subtitle={`${ticket.replies?.length || 0} message(s)`} icon="💬">
        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
          {(!ticket.replies || ticket.replies.length === 0) && (
            <div className="text-[12px] py-4 text-center" style={{ color: MUTED }}>
              No replies yet. Our team will respond soon.
            </div>
          )}
          {ticket.replies?.map((r, i) => (
            <div
              key={i}
              className="rounded-lg p-4"
              style={{
                background: r.authorRole === 'agent' || r.authorRole === 'admin' ? '#f0f7f7' : '#fdfbf6',
                border: `1px solid ${BORDER}`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[12px]" style={{ color: TEXT }}>
                  {r.author?.fullName || 'Unknown'}
                </span>
                {(r.authorRole === 'agent' || r.authorRole === 'admin') && (
                  <Pill tone="teal">Team</Pill>
                )}
                <span className="text-[10.5px] ml-auto" style={{ color: MUTED }}>
                  {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: TEXT }}>
                {r.message}
              </div>
            </div>
          ))}
        </div>

        {/* Reply form */}
        {ticket.status !== 'closed' && (
          <form onSubmit={sendReply} className="mt-4 pt-4 border-t" style={{ borderColor: BORDER }}>
            <div className={labelClass}>
              Add a reply
            </div>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write your follow-up message…"
              rows={3}
              className={textareaClass}
            />
            <div className="mt-2 flex items-center gap-2">
              <Button type="submit" size="sm" loading={sending} disabled={!reply.trim()}>
                {sending ? 'Sending…' : 'Send Reply'}
              </Button>
            </div>
          </form>
        )}
      </ProfileCard>
    </div>
  );
}

export default function SupportPage() {
  // Seed from cache for the default 'all' view so a revisit paints instantly.
  const seeded = getView(supportTicketsKey('all'));
  const [tickets, setTickets] = useState(() => seeded?.tickets ?? []);
  const [counts, setCounts] = useState(() => seeded?.counts ?? { all: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [loading, setLoading] = useState(() => !hasView(supportTicketsKey('all')));

  const fetchTickets = async () => {
    const key = supportTicketsKey(statusFilter);
    if (!hasView(key)) setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await ticketAPI.getMyTickets(params);
      if (res.success) {
        setTickets(res.data.tickets);
        setCounts(res.data.counts);
        setView(key, { tickets: res.data.tickets, counts: res.data.counts });
      }
    } catch (err) {
      // Keep the cached list on a failed refresh.
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  return (
    <AppLayout pageTitle="Help & Support">
      <div className="text-[26px] font-bold text-gray-900 mb-[6px]" style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '.2px' }}>
        Help & Support
      </div>
      <div className="h-[2px] w-[64px] mb-3" style={{ background: 'linear-gradient(90deg,#b8973a 0%,transparent 100%)', borderRadius: 2 }} />
      <div className="text-[13px] text-gray-500 mb-6">
        Raise a ticket, track progress, and chat with our team.
      </div>

      {selectedTicketId ? (
        <TicketThread
          ticketId={selectedTicketId}
          onBack={() => setSelectedTicketId(null)}
        />
      ) : (
        <div className="space-y-6">
          <CreateTicketForm onCreated={fetchTickets} />
          {loading ? (
            <ProfileCard title="Loading tickets…" icon="⏳">
              <div className="text-[13px] py-6" style={{ color: MUTED }}>Loading your tickets…</div>
            </ProfileCard>
          ) : (
            <TicketList
              tickets={tickets}
              counts={counts}
              statusFilter={statusFilter}
              onFilter={setStatusFilter}
              onSelect={setSelectedTicketId}
              selectedId={selectedTicketId}
            />
          )}
        </div>
      )}
    </AppLayout>
  );
}
